import type { Variant } from "@components/commerce/product/product-tile/ProductTile";
import { ProductTile } from "@components/commerce/product/product-tile/ProductTile";
import { useGlobalModalContext } from "@components/GlobalModalProvider";
import type { Document } from "@contentful/rich-text-types";
import { AnalyticsClient } from "@lib/analytics/AnalyticsClient";
import { CommonCMS } from "@lib/constants/contentful";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { ModalTypes } from "@lib/enums/ModalTypes";
import { hasOneSize } from "@lib/utils/productUtility";
import {
    addToMyShoppingList,
    createMyShoppingList,
    removeFromMyShoppingList,
    showErrorToaster,
    signInAndGetSession,
} from "@lib/utils/shoppingList-utility/shoppingListUtility";
import { useErrorToast } from "@lib/utils/store-utility/storeUtility";
import {
    useAddProductAndRemoveOldestFromWishlist,
    useAddProductToWishlist,
    useCreateMyWishlist,
    useGetMyActiveShoppingList,
    useLock,
    useRemoveProductFromWishlist,
} from "@store/hooks";
import { useStore } from "@store/index";
import { Box } from "@ui/components/layout/box/Box";
import { PRODUCT_IMAGE_PLACEHOLDER_URL } from "@ui/components/media-and-icons/image/imagePaths";
import hexRgb from "hex-rgb";
import { useSession } from "next-auth/react";
import { useRouter } from "next/router";
import type { FC } from "react";
import { useEffect, useMemo, useRef } from "react";

interface IProductHitsProps {
    productListHits: any[];
    shouldSaveLastVisitedPath?: boolean;
    productListName?: string;
    authorizationInvitation?: Document;
    containerHeight?: number;
    isFromSearch?: boolean;
}

export const ProductHits: FC<IProductHitsProps> = ({
    productListHits,
    shouldSaveLastVisitedPath = false,
    productListName = "",
    containerHeight,
    isFromSearch = false,
}) => {
    const errorToaster = useErrorToast();
    const lock = useLock();
    const { data: session } = useSession();
    const { locale, asPath } = useRouter();
    const { get: getMicrocopy } = useMicrocopy();
    const setWishlistIsExecuted = useStore((state) => state.setWishlistIsExecuted);
    const setLastVisitedPlp = useStore((state) => state.setLastVisitedPlp);
    const setLastLoadedPlpContainerHeight = useStore(
        (state) => state.setLastLoadedPlpContainerHeight
    );
    const currentFilters = useStore((state) => state.data.products?.currentFilters);
    const { showModal } = useGlobalModalContext();

    const lastAppliedFilter = currentFilters[currentFilters.length - 1];
    const wishlist = useStore((state) => state.data.me.wishlistInfo.wishlist);
    const getWishlist = useStore((state) => state.getWishlist);

    const intersectItems = useRef(new Set());

    const executeGetMyActiveWishlist = useGetMyActiveShoppingList();
    const executeCreateMyWishlist = useCreateMyWishlist();
    const executeAddProductToWishlist = useAddProductToWishlist();
    const executeRemoveProductFromWishlist = useRemoveProductFromWishlist();
    const executeAddProductAndRemoveOldestFromWishlist = useAddProductAndRemoveOldestFromWishlist();

    const addProductHitToWishlist = async (hit, index) => {
        const wishlist = getWishlist();
        const foundItem = wishlist?.lineItems?.find(
            (x) =>
                x.variant?.sku === hit.objectID ||
                hit.variants?.some((v: Variant) => v.sku === x.variant?.sku)
        );
        const sku =
            hit.variants?.find((v: Variant) => hasOneSize(v.size))?.sku ??
            foundItem?.variant?.sku ??
            hit.objectID;

        if (foundItem) {
            await Promise.all([
                removeFromMyShoppingList(
                    locale,
                    foundItem,
                    wishlist,
                    executeRemoveProductFromWishlist
                ),
                AnalyticsClient.removeFromWishList(hit, {
                    listName: productListName,
                    position: index,
                    lastAppliedFilter,
                }),
            ]);
        } else {
            await Promise.all([
                AnalyticsClient.addToWishList(hit, {
                    listName: productListName,
                    position: index,
                    lastAppliedFilter,
                }),
                addToMyShoppingList(
                    locale,
                    wishlist,
                    sku,
                    executeAddProductToWishlist,
                    executeAddProductAndRemoveOldestFromWishlist
                ),
            ]);
        }
    };

    useEffect(() => {
        if (!session) {
            signInAndGetSession(getMicrocopy, errorToaster);
        }
    }, []);

    const handleAddProductHitToWishlist = lock(async (hit, index) => {
        setWishlistIsExecuted(false);

        if (!getWishlist()) {
            await createMyShoppingList(locale, getMicrocopy, executeCreateMyWishlist, errorToaster);
        }

        try {
            await addProductHitToWishlist(hit, index);
        } catch (error) {
            showErrorToaster(getMicrocopy, errorToaster);
        }
        setWishlistIsExecuted(true);
    });

    useEffect(() => {
        if (productListHits.length) {
            AnalyticsClient.loadItemsList(productListHits, {
                listName: productListName,
                lastAppliedFilter,
            });
        }
    }, [lastAppliedFilter, productListHits, productListName]);

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            AnalyticsClient.viewItemsList(
                productListHits.filter((item) => intersectItems.current.has(item.objectID)),
                { listName: productListName, lastAppliedFilter }
            );
        }, 2000);
        return () => clearTimeout(timeoutId);
    }, [intersectItems.current, lastAppliedFilter, productListHits, productListName]);

    const output = useMemo(
        () =>
            productListHits.map((hit, index) => {
                const hasStockCount =
                    (hit.productColors || []).filter((color) => color.hasStock === true).length + 1;
                const colorLabel = `${hasStockCount} ${getMicrocopy(
                    CommonCMS.shared,
                    hasStockCount === 1 ? CommonCMS.color : CommonCMS.colors
                )}`;

                const existOne = wishlist?.lineItems?.find(
                    (x) =>
                        x.variant?.sku === hit.objectID ||
                        hit.variants?.some((v: Variant) => v.sku === x.variant?.sku)
                );

                return (
                    <Box key={hit.objectID}>
                        <ProductTile
                            height={["110vw", "80vw", "55vw", "45vw", "42vw"]}
                            position={hit.__position}
                            productName={hit.name}
                            articleNumber={hit.articleNumber}
                            colorCode={hit.colorCode}
                            image={hit.image}
                            imageLabel={hit.imageLabel}
                            variants={hit.variants}
                            altTagPattern={`${hit.pageTitle ?? hit.name} - ${hit.dominantColor}`}
                            isAddingToWishlist={Boolean(existOne)}
                            onAddToWishlist={async () => {
                                const foundItem = wishlist?.lineItems.find(
                                    (x) =>
                                        x.variant?.sku === hit.objectID ||
                                        hit.variants?.some((v: Variant) => v.sku === x.variant?.sku)
                                );
                                const sku =
                                    hit.variants?.find((v: Variant) => hasOneSize(v.size))?.sku ??
                                    foundItem?.variant?.sku ??
                                    hit.objectID;
                                await executeGetMyActiveWishlist();
                                showModal(ModalTypes.addToWishList, {
                                    productKey: sku,
                                    selectedProductSku: sku,
                                });
                                handleAddProductHitToWishlist(hit, index);
                            }}
                            productColors={hit.productColors.map((it) => ({
                                priceWithDiscount: it.priceRaw,
                                priceWithoutDiscount: it.listPriceRaw,
                                image: it.image || PRODUCT_IMAGE_PLACEHOLDER_URL,
                                imageLabel: it.imageLabel,
                                altTagPattern: `${hit.pageTitle ?? hit.productName} - ${it.dominantColor}`,
                                objectID: it.objectID,
                                colorCode: it.colorCode,
                                imageBackgroundColor: hexRgb(it.backgroundColor, {
                                    format: "css",
                                    alpha: 1,
                                }),
                                discountPercentage: it.discountPercentage,
                                lowestPrice: it.lowestPriceRaw,
                                discountPercentageLowestPrice: it.discountPercentageLowestPrice,
                                hasDiscount: it.hasDiscount,
                                hasStock: it.hasStock,
                            }))}
                            objectID={hit.objectID}
                            locale={locale}
                            priceWithoutDiscount={hit.listPriceRaw}
                            priceWithDiscount={hit.priceRaw}
                            discountPercentage={hit.discountPercentage}
                            lowestPrice={hit.lowestPriceRaw}
                            discountPercentageLowestPrice={hit.discountPercentageLowestPrice}
                            colorLabel={colorLabel}
                            imageBackgroundColor={hexRgb((hit.backgroundColor ?? "FFF") as string, {
                                format: "css",
                                alpha: 1,
                            })}
                            customBadges={hit.badges}
                            descriptiveHeading={hit.descriptiveHeading}
                            handleTileClick={() => {
                                setLastLoadedPlpContainerHeight(containerHeight);
                                // We need to do this check as /search page and search from the navigation loads PLPs.
                                // When that happens, we don't want to save the last visited path as it breaks the breadcrumb logic.
                                // When lastVisitedPlp is set to null, it will use the fallback breadcrumbs which are Algolia most common categories.
                                setLastVisitedPlp(
                                    asPath.startsWith("/search") ||
                                        asPath.startsWith("/product") ||
                                        isFromSearch
                                        ? null
                                        : // Decode URL for special chars vaba%C3%B5hutegevus -> vabaõhutegevus
                                          decodeURI(asPath)
                                );
                                AnalyticsClient.selectItem(
                                    hit,
                                    {
                                        listName: productListName,
                                        lastAppliedFilter,
                                        position: index,
                                    },
                                    hit.__queryID ?? hit.__autocomplete_queryID
                                );
                            }}
                            onInViewPort={(inView, item) => {
                                if (inView) {
                                    intersectItems.current.add(item);
                                } else {
                                    intersectItems.current.delete(item);
                                }
                            }}
                            hasStock={hit.hasStock}
                        />
                    </Box>
                );
            }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [productListHits, productListName, shouldSaveLastVisitedPath, wishlist, containerHeight]
    );

    return <>{output}</>;
};
